import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  DatePicker,
  Divider,
  Flex,
  Form,
  FormItemProps,
  FormProps,
  Input,
  message,
  Radio,
  Spin,
  Typography,
} from 'antd';
import AvatarUpload from 'components/AvatarUpload';
import FileUploadInput from 'components/FileUploadInput';
import FormContainer from 'components/FormContainer';
import Select from 'components/Select';
import dayjs from 'dayjs';
import useAgronomistList from 'hooks/useAgronomistList';
import useBankNameList from 'hooks/useBankNameList';
import useUploadDocument from 'hooks/useUploadDocument';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { routes } from 'routes/AllRoutes';
import useMutateVerifyOpenAI from 'screens/admin-review/hooks/useMutateVerifyOpenAI';
import useFarmerGroup from 'screens/farmer-groups/hooks/useFarmerGroupListQuery';
import AddressForm from 'screens/stakeholders/components/AddressForm';
import {
  convertMachineOwnerToForm,
  validateNationalID,
  validatePhoneNumber,
} from 'screens/stakeholders/helper';
import useMachineTypeQuery from 'screens/stakeholders/hooks/queries/useMachineTypeQuery';
import useMutateMachineOwner, {
  MachineOwnerPayload,
} from 'screens/stakeholders/hooks/mutations/useMutateMachineOwner';
import { selectToken } from 'store/auth/selectors';
import { formProps, SUPPORTED_COUNTRIES } from 'utils/constants';
import {
  getCountryCurrency,
  getCountryPhoneCode,
  getRoleFromToken,
  populateOptions,
} from 'utils/helpers';
import { useStakeholderFileManagement } from 'screens/stakeholders/hooks/useStakeholderFileManagement';
import useMachineOwnerById from 'screens/stakeholders/hooks/queries/useMachineOwnerById';

const breadcrumbItems = (isEdit: boolean) => [
  {
    title: (
      <Link className="link" to="/stakeholders/machine-owner">
        Farmer Stakeholder Management
      </Link>
    ),
  },
  {
    title: isEdit ? 'Edit' : 'Add Individually',
  },
];

const MACHINE_AGE_OPTIONS = [
  { value: '<1 year', label: '<1 year' },
  { value: '1 year', label: '1 year' },
  { value: '2 years', label: '2 years' },
  { value: '3 years', label: '3 years' },
  { value: '4 years', label: '4 years' },
  { value: '5 years', label: '5 years' },
  { value: '>5 years', label: '>5 years' },
];

const MACHINE_AVAILABILITY_OPTIONS = [
  { value: 'FULL', label: 'Full-time (all year)' },
  { value: 'SEASONAL', label: 'Seasonal' },
];

const initialValues = {
  machinesOwned: [{}],
};

export type MachineOwnerFormObject = MachineOwnerPayload & {
  isRelatedToEmployee: boolean;
  contractStartDate?: dayjs.Dayjs;
  agronomistId?: string;
  deletedMachineOwnedIds?: string[];
};

const FormItem = (props: FormItemProps) =>
  Form.Item({ ...formProps, ...props });

const MachineOwnerForm = ({ edit = false }: { edit?: boolean }) => {
  // const [accountBookPicture, setAccountBookPicture] = useState<Blob>();

  const [isLoadingData, setIsLoadingData] = useState(edit);
  const [isTouched, setIsTouched] = useState(false);
  const [isDraft, setIsDraft] = useState(false);

  const [searchFarmerGroup, setSearchFarmerGroup] = useState<string>();

  const [deletedMachineOwnedIds, setDeletedMachineOwnedIds] = useState<
    string[]
  >([]);

  const [form] = Form.useForm();
  const country = Form.useWatch(['address', 'country'], form);

  const {
    existingPicture,
    profilePicture,
    existingNationalIdPictures,
    nationalIdPictures,
    uploadedFirstPageContract,
    existingContractPictures,
    firstPageContract,
    contractPictures,
    setProfilePicture,
    setNationalIdPictures,
    setDeletedIdPictures,
    setFirstPageContract,
    setContractPictures,
    setDeletedContractPictures,
    validateFiles,
    initializeFiles,
    handleDeleteFile,
  } = useStakeholderFileManagement(edit, country);

  const navigate = useNavigate();
  const { id } = useParams();

  const isRelatedToEmployee = Form.useWatch('isRelatedToEmployee', form);

  const role = getRoleFromToken(useSelector(selectToken));
  const isUserPM = role === 'PM';

  const { data: machineOwnerData, isPending: isLoadingFetchMO } =
    useMachineOwnerById(id, edit);

  const { agronomistFilterOptions, isLoading: isAgronomistLoading } =
    useAgronomistList(country, {
      role: 'sr',
      labelKey: 'email',
      valueKey: 'id',
    });

  const { bankNameOptions, isLoading: isLoadingBank } = useBankNameList();
  const { farmerGroupOptions, isLoading: isLoadingFarmerGroup } =
    useFarmerGroup({
      country,
      search: searchFarmerGroup,
    });
  const { machineTypeOptions, isLoading: isLoadingMachineType } =
    useMachineTypeQuery();

  const preFilledFGOption = useMemo(() => {
    if (machineOwnerData?.data?.preferredFarmerGroup) {
      return {
        label: machineOwnerData.data.preferredFarmerGroup.name,
        value: machineOwnerData.data.preferredFarmerGroup.id,
      };
    }
  }, [machineOwnerData]);

  const populatedFgOptions = useMemo(
    () =>
      populateOptions(
        preFilledFGOption,
        farmerGroupOptions,
        isLoadingFarmerGroup,
      ),
    [farmerGroupOptions],
  );

  const { mutateAsync: mutateVerifyOpenAI } = useMutateVerifyOpenAI({
    showVerificationNotification: true,
  });

  const { mutateAsync: mutateUploadFile, isPending: isUploadingFile } =
    useUploadDocument();

  const { mutate: mutateMachineOwner, isPending: isSubmittingData } =
    useMutateMachineOwner({
      onSuccess: async (data) => {
        if (profilePicture) {
          await mutateUploadFile({
            file: profilePicture,
            objectClass: 'MachineOwner',
            objectId: data?.data?.id,
            tag: 'PROFILE',
          });
        }

        if (nationalIdPictures.length) {
          await uploadNationalIdPictures(data?.data?.id);
        }

        if (firstPageContract) {
          await mutateUploadFile({
            file: firstPageContract,
            objectClass: 'MachineOwner',
            objectId: data?.data?.id,
            tag: 'FIRST_PAGE_CONTRACT',
          });
        }

        if (contractPictures.length) {
          await uploadContractPictures(data?.data?.id);
        }

        mutateVerifyOpenAI({
          stakeholderId: data?.data?.id,
          stakeholderType: 'MACHINE_OWNER',
          verificationTrigger: edit ? 'AUTOMATIC_UPDATE' : 'AUTOMATIC_CREATE',
        });

        return setTimeout(() => {
          navigate(routes.machineOwner);
        }, 500);
      },
      onFinish: () => {
        message.destroy('submit-machine-owner');
      },
      method: edit ? 'PUT' : 'POST',
    });

  const uploadNationalIdPictures = async (id: string) => {
    for (const file of nationalIdPictures) {
      try {
        await mutateUploadFile({
          file: file,
          objectClass: 'MachineOwner',
          objectId: id,
          tag: 'NATIONAL_ID',
        });
      } catch {
        message.error('Failed to upload National ID');
      }
    }
  };

  const uploadContractPictures = async (id: string) => {
    for (const file of contractPictures) {
      try {
        await mutateUploadFile({
          file: file,
          objectClass: 'MachineOwner',
          objectId: id,
          tag: 'SIGNED_CONTRACT',
        });
      } catch {
        message.error('Failed to upload Signed Contracts');
      }
    }
  };

  const getFormattedData = (values: MachineOwnerFormObject) => {
    const { accountHolderName, accountNumber, paymentType } = {
      ...values.bankAccountInfo,
    };
    const bankAccountInfo =
      accountHolderName && accountNumber && paymentType?.id
        ? values.bankAccountInfo
        : undefined;

    return {
      ...values,
      bankAccountInfo: bankAccountInfo,
      preferredFarmerGroup: values.preferredFarmerGroup?.id
        ? { id: values.preferredFarmerGroup.id }
        : undefined,
      id: edit ? machineOwnerData?.data?.id : undefined,
      contractStartDate: values.contractStartDate?.toISOString(),
      phoneNumber: getCountryPhoneCode(country) + values.phoneNumber,
      deletedMachineOwnedIds: edit ? deletedMachineOwnedIds : undefined,
    };
  };
  const onFinish: FormProps<MachineOwnerFormObject>['onFinish'] = async (
    values,
  ) => {
    const formattedData = getFormattedData(values);
    if (validateFiles()) {
      message.loading({
        key: 'submit-machine-owner',
        content: 'Uploading stakeholder data and files...',
        duration: 0,
      });
      if (edit) await handleDeleteFile();
      mutateMachineOwner(formattedData);
    }
  };

  useEffect(() => {
    if (!edit || !machineOwnerData?.data) return;
    const convertedData = convertMachineOwnerToForm(machineOwnerData.data);

    form.setFieldsValue({
      ...convertedData,
    });

    if (convertedData.machinesOwned?.length) {
      form.setFieldValue('machinesOwned', convertedData.machinesOwned);
    }

    initializeFiles(machineOwnerData.data, form);

    if (machineOwnerData?.data?.contractStartDate) {
      form.setFieldValue(
        ['contractStartDate'],
        dayjs(machineOwnerData?.data?.contractStartDate),
      );
    }

    form.setFieldValue(
      'isRelatedToEmployee',
      !!machineOwnerData?.data?.relatedRizeEmployee,
    );

    setTimeout(() => setIsLoadingData(false), 1000);
  }, [edit, machineOwnerData?.data]);

  const onFieldsChanges = () => {
    if (edit && !isLoadingData) {
      setIsTouched(true);
    }
  };

  return (
    <>
      <Spin spinning={edit && isLoadingFetchMO} fullscreen />
      <FormContainer
        title="Add Machine Owner"
        form={form}
        breadcrumbItems={breadcrumbItems(edit)}
        onFieldsChange={onFieldsChanges}
        cancelBtnProps={{
          disabled: isSubmittingData || isUploadingFile || isDraft,
        }}
        submitBtnProps={{
          loading: isUploadingFile || isLoadingData || isSubmittingData,
        }}
        onCancel={() => navigate(routes.machineOwner)}
        onFinish={onFinish}
        draftBtnProps={{
          disabled: (edit && !isTouched) || isSubmittingData || isUploadingFile,
          loading: (isSubmittingData || isUploadingFile) && isDraft,
          // onClick: handleSaveForLater,
        }}
      >
        <Card>
          <Typography.Title level={5} style={{ marginBottom: '24px' }}>
            Personal Data
          </Typography.Title>
          <FormItem label="Profile Picture">
            <AvatarUpload
              file={profilePicture}
              setFile={(file) => {
                setProfilePicture(file);
                setIsTouched(true);
              }}
              existingPicture={existingPicture}
            />
          </FormItem>
          <FormItem
            name={['address', 'country']}
            label="Country"
            required
            rules={[{ required: true }]}
          >
            <Select
              placeholder="Select country"
              showSearch
              disabled={edit}
              options={SUPPORTED_COUNTRIES}
            />
          </FormItem>
          <FormItem
            name="name"
            label="Name"
            required
            rules={[{ required: true }]}
          >
            <Input placeholder="Input name" />
          </FormItem>
          <FormItem
            name="phoneNumber"
            label="Phone Number"
            required
            hasFeedback
            dependencies={[['address', 'country']]}
            rules={[
              { required: true },
              {
                pattern: /^[0-9]*$/,
                message: 'Only Numbers are allowed',
              },
              {
                max: 14,
                min: 8,
                message: 'Phone number must be between 8-14 digits',
              },
              {
                validator: (_, value) =>
                  validatePhoneNumber(value, 'machine-owner', country),
              },
            ]}
            validateDebounce={300}
            normalize={(value, prevValue) =>
              /^[0-9]*$/.test(value) || value === '' ? value : (prevValue ?? '')
            }
          >
            <Input
              prefix={
                getCountryPhoneCode(country) &&
                `+${getCountryPhoneCode(country)}`
              }
              type="tel"
              placeholder=" Input phone number here"
            />
          </FormItem>
          <FormItem
            name="nationalIdNumber"
            label="National ID Number"
            required={country !== 'Vietnam'}
            hasFeedback
            dependencies={[['address', 'country']]}
            rules={[
              {
                pattern: /^[0-9]*$/,
                message: 'Only Numbers are allowed',
              },
              {
                max: country !== 'Vietnam' ? 16 : 12,
                min: country !== 'Vietnam' ? 16 : 12,
                message: `ID number must be ${
                  country !== 'Vietnam' ? 16 : 12
                } digits`,
              },
              {
                validator: (_, value) =>
                  validateNationalID(
                    value,
                    'machine-owner',
                    country,
                    machineOwnerData?.data?.nationalIdNumber,
                  ),
              },
            ]}
            validateDebounce={300}
            normalize={(value, prevValue) =>
              /^[0-9]*$/.test(value) || value === '' ? value : (prevValue ?? '')
            }
          >
            <Input
              showCount
              maxLength={country !== 'Vietnam' ? 16 : 12}
              placeholder="Input National ID Number"
            />
          </FormItem>

          <Form.Item
            label="National ID Picture"
            name="nationalIdPicture"
            required={country !== 'Vietnam'}
            rules={[{ required: country !== 'Vietnam' }]}
            {...formProps}
          >
            <FileUploadInput
              multiple
              listType="picture"
              uploadShape="drag"
              defaultFileList={existingNationalIdPictures}
              disabled={edit && machineOwnerData?.data?.status === 'active'}
              onFileChange={(files) => {
                setNationalIdPictures(files);
                setIsTouched(true);
              }}
              onDelete={(file) => {
                if (
                  existingNationalIdPictures.find((f) => f.uid === file.uid)
                ) {
                  setDeletedIdPictures((prev) => [...prev, file]);
                  setIsTouched(true);
                }
              }}
            />
          </Form.Item>

          {isUserPM ? (
            <Form.Item
              name="agronomistId"
              label="Senior Agronomist Email"
              {...formProps}
            >
              <Select
                placeholder="Select Email"
                showSearch
                options={agronomistFilterOptions}
                loading={isAgronomistLoading}
              />
            </Form.Item>
          ) : null}
        </Card>

        <AddressForm
          form={form}
          type="farmerGroupLeader"
          autoReset={!edit || !isLoadingData}
        />

        <Card>
          <Form.List
            name="machinesOwned"
            initialValue={
              edit
                ? machineOwnerData?.data?.machinesOwned
                : initialValues.machinesOwned
            }
          >
            {(fields, { add, remove }) => (
              <>
                <Flex justify="space-between" align="middle">
                  <Typography.Title level={5} style={{ marginBottom: '24px' }}>
                    Machine Details
                  </Typography.Title>
                  <Button
                    onClick={() => add()}
                    type="primary"
                    icon={<PlusOutlined />}
                  >
                    Add Machine
                  </Button>
                </Flex>
                {fields.map(({ key, name }, index) => (
                  <Form.Item key={key} style={{ position: 'relative' }}>
                    <FormItem name={[name, 'id']} style={{ display: 'none' }} />
                    <FormItem
                      name={[name, 'machine', 'id']}
                      label="Machine Type"
                      required
                      rules={[{ required: true }]}
                    >
                      <Select
                        placeholder="Select type"
                        showSearch
                        loading={isLoadingMachineType}
                        options={machineTypeOptions}
                      />
                    </FormItem>
                    <FormItem
                      name={[name, 'age']}
                      label="Machine Age"
                      required
                      rules={[{ required: true }]}
                    >
                      <Select
                        placeholder="Select age of machine"
                        showSearch
                        options={MACHINE_AGE_OPTIONS}
                      />
                    </FormItem>
                    <FormItem
                      name={[name, 'quantity']}
                      label="Machine Quantity"
                      required
                      rules={[
                        {
                          pattern: /^[0-9]*$/,
                          message: 'Only Numbers are allowed',
                        },
                        { required: true },
                      ]}
                      normalize={(value, prevValue) =>
                        /^[0-9]*$/.test(value) || value === ''
                          ? value
                          : (prevValue ?? '')
                      }
                    >
                      <Input placeholder="Number of machine owned" />
                    </FormItem>
                    <FormItem
                      name={[name, 'pricePerHa']}
                      label="Unit price per ha"
                      required
                      rules={[
                        {
                          pattern: /^[0-9]*$/,
                          message: 'Only Numbers are allowed',
                        },
                        { required: true },
                      ]}
                      normalize={(value, prevValue) =>
                        /^[0-9]*$/.test(value) || value === ''
                          ? value
                          : (prevValue ?? '')
                      }
                    >
                      <Input
                        prefix={getCountryCurrency(country)}
                        placeholder="Machine service rent per ha"
                      />
                    </FormItem>
                    <FormItem
                      name={[name, 'availability']}
                      label="Machine Availability"
                    >
                      <Select
                        placeholder="Select availability"
                        showSearch
                        options={MACHINE_AVAILABILITY_OPTIONS}
                      />
                    </FormItem>
                    {fields?.length > 1 && index < fields.length - 1 && (
                      <Divider style={{ marginTop: '48px' }} />
                    )}
                    {index > 0 && (
                      <Button
                        icon={<DeleteOutlined />}
                        danger
                        onClick={() => {
                          const machineOwnedId = form.getFieldValue([
                            'machinesOwned',
                            index,
                            'id',
                          ]);
                          if (edit && machineOwnedId) {
                            setDeletedMachineOwnedIds((prev) => [
                              ...prev,
                              machineOwnedId,
                            ]);
                          }
                          remove(index);
                        }}
                        style={{
                          position: 'absolute',
                          right: 0,
                          top: 0,
                        }}
                      ></Button>
                    )}
                  </Form.Item>
                ))}
              </>
            )}
          </Form.List>
        </Card>

        <Card>
          <Typography.Title level={5} style={{ marginBottom: '24px' }}>
            Bank Information
          </Typography.Title>
          <FormItem
            name={['bankAccountInfo', 'paymentType', 'id']}
            label="Bank Name"
          >
            <Select
              allowClear
              showSearch
              placeholder="Select Bank Name"
              options={bankNameOptions}
              loading={isLoadingBank}
            />
          </FormItem>
          <FormItem
            name={['bankAccountInfo', 'accountNumber']}
            label="Account Number"
            rules={[
              {
                pattern: /^[0-9]*$/,
                message: 'Only Numbers are allowed',
              },
            ]}
            normalize={(value, prevValue) =>
              /^[0-9]*$/.test(value) || value === '' ? value : (prevValue ?? '')
            }
          >
            <Input placeholder="Input bank account number" />
          </FormItem>
          <FormItem
            name={['bankAccountInfo', 'accountHolderName']}
            label="Account Holder Name"
            normalize={(value) => value && value?.toUpperCase()}
          >
            <Input placeholder="Account Holder Name" />
          </FormItem>
          <FormItem
            name={['bankAccountInfo', 'accountBookPicture']}
            label="Account Book Picture"
          >
            <FileUploadInput
              uploadShape="button"
              // onFileChange={(file) => {
              //   setAccountBookPicture(file);
              // }}
              buttonText="Click to Upload (Max. 1)"
            />
          </FormItem>
        </Card>

        <Card>
          <Typography.Title level={5} style={{ marginBottom: '24px' }}>
            Other Details
          </Typography.Title>
          <FormItem
            name="isRelatedToEmployee"
            label="Relationship with Employee"
            required
            rules={[{ required: true }]}
            wrapperCol={{ span: 24 }}
          >
            <Radio.Group
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
              options={[
                {
                  value: true,
                  label:
                    'Yes, stakeholder have close relationship with Rize’s employee',
                },
                {
                  value: false,
                  label:
                    'No, stakeholder doesn’t have close relationship with Rize’s employee ',
                },
              ]}
            />
          </FormItem>
          {isRelatedToEmployee && (
            <FormItem
              name="relatedRizeEmployee"
              label="Employee Name"
              required
              rules={[{ required: true }]}
            >
              <Input placeholder="Name of the employee with relationship" />
            </FormItem>
          )}
          <FormItem
            name={['preferredFarmerGroup', 'id']}
            label="Preferred Farmer Group"
          >
            <Select
              placeholder="Select preferred farmer group for this machine."
              showSearch
              allowClear
              loading={isLoadingFarmerGroup}
              onSearch={(value) => setSearchFarmerGroup(value)}
              options={populatedFgOptions}
            />
          </FormItem>
        </Card>

        <Card>
          <Typography.Title level={5} style={{ marginBottom: '24px' }}>
            Upload Contracts
          </Typography.Title>

          <FormItem
            name="firstPageContract"
            label="First Page Contract"
            required={country !== 'Vietnam'}
            rules={[
              {
                required: country !== 'Vietnam',
              },
            ]}
          >
            <FileUploadInput
              listType="picture"
              defaultFileList={
                uploadedFirstPageContract ? [uploadedFirstPageContract] : []
              }
              uploadShape="drag"
              onFileChange={(files) => {
                setFirstPageContract(files);
                setIsTouched(true);
              }}
              onDelete={(file) => {
                if (existingContractPictures.find((f) => f.uid === file.uid)) {
                  setDeletedContractPictures((prev) => [...prev, file]);
                  setIsTouched(true);
                }
              }}
            />
          </FormItem>

          <FormItem
            name="contractPicture"
            label="Signed Contract"
            required={country !== 'Vietnam'}
            rules={[
              {
                required: country !== 'Vietnam',
              },
            ]}
          >
            <FileUploadInput
              multiple
              listType="picture"
              defaultFileList={existingContractPictures}
              uploadShape="drag"
              onFileChange={(files) => {
                setContractPictures(files);
                setIsTouched(true);
              }}
              onDelete={(file) => {
                if (existingContractPictures.find((f) => f.uid === file.uid)) {
                  setDeletedContractPictures((prev) => [...prev, file]);
                  setIsTouched(true);
                }
              }}
            />
          </FormItem>

          <Form.Item
            name="contractStartDate"
            label="Effective From"
            required={country !== 'Vietnam'}
            rules={[
              {
                required: country !== 'Vietnam',
              },
            ]}
            {...formProps}
          >
            <DatePicker format="DD MMMM, YYYY" />
          </Form.Item>
        </Card>
      </FormContainer>
    </>
  );
};

export default MachineOwnerForm;
